import { useEffect, useRef, useState } from 'react'

import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import type { SxProps, Theme } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'

const PREVIEW_HEIGHT = 60

type DeviceStatus = 'loading' | 'active' | 'error' | 'disconnected'

export type VideoDevicePreviewProps = {
  deviceId: string
  sx?: SxProps<Theme>
}

export function VideoDevicePreview({ deviceId, sx = [] }: VideoDevicePreviewProps) {
  const { t } = useTranslation()
  const videoRef = useRef<HTMLVideoElement>(null)
  const [status, setStatus] = useState<DeviceStatus>('loading')

  useEffect(() => {
    let cancelled = false
    let stream: MediaStream | null = null

    const startStream = async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { deviceId: { exact: deviceId } },
        })
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop())
          return
        }

        // Listen for track ending (device unplugged, permissions revoked)
        const track = stream.getVideoTracks()[0]
        if (track) {
          track.addEventListener('ended', () => {
            if (!cancelled) setStatus('disconnected')
          })
        }

        if (videoRef.current) {
          videoRef.current.srcObject = stream
        }
        if (!cancelled) setStatus('active')
      } catch (err) {
        console.warn(`Could not access video device ${deviceId}:`, err)
        if (!cancelled) setStatus('error')
      }
    }

    startStream()

    return () => {
      cancelled = true
      if (videoRef.current) {
        videoRef.current.srcObject = null
      }
      stream?.getTracks().forEach((t) => t.stop())
    }
  }, [deviceId])

  const showFallback = status === 'error' || status === 'disconnected'

  if (showFallback) {
    return (
      <Box
        sx={[
          {
            width: `${PREVIEW_HEIGHT * (16 / 9)}px`,
            height: `${PREVIEW_HEIGHT}px`,
            borderRadius: '4px',
            backgroundColor: 'rgba(0,0,0,0.04)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            ...(status === 'disconnected' ? { border: '1px solid', borderColor: 'error.main' } : {}),
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      >
        <Typography variant="caption" sx={{ color: status === 'disconnected' ? 'error.main' : 'text.secondary', fontSize: '10px', textAlign: 'center', px: 0.5 }}>
          {status === 'disconnected'
            ? t('component.ui.deviceSelectionDialog.deviceDisconnected')
            : t('component.ui.deviceSelectionDialog.noVideoDetected')}
        </Typography>
      </Box>
    )
  }

  return (
    <Box
      component="video"
      ref={videoRef}
      autoPlay
      muted
      playsInline
      sx={[
        {
          width: `${PREVIEW_HEIGHT * (16 / 9)}px`,
          height: `${PREVIEW_HEIGHT}px`,
          borderRadius: '4px',
          objectFit: 'cover',
          backgroundColor: 'rgba(0,0,0,0.04)',
          flexShrink: 0,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    />
  )
}

export default VideoDevicePreview
